import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    Typography,
    TextField,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider,
} from "@mui/material";
import { Participant } from "common/build/prisma/client";
import { formatParticipantReference } from "common/build/utils/participantReference";
import { list as getParticipants } from "../../client/api/participants";
import Spinner from "../layout/Spinner";
import { useAuth } from "../../contexts/auth.context";
import { useMobile } from "../../hooks/useMobile";
import { useAppNavigate } from "../../hooks/useAppNavigate";

type ParticipantWithProgrammes = Participant & {
    ParticipantToProgramme?: {
        Programme: {
            id: number;
            name: string;
            isGroup: boolean;
        };
    }[];
};

type ParticipantWithClassrooms = Participant & {
    ParticipantToClassroom?: {
        Classroom: {
            id: number;
            name: string;
        };
    }[];
};

export const MyParticipants: React.FC = () => {
    const { user } = useAuth();
    const { push } = useAppNavigate();
    const { isMobile, isTablet } = useMobile();
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchQuery, setSearchQuery] = useState<string>("");

    useEffect(() => {
        const fetchParticipants = async () => {
            try {
                const response = await getParticipants(
                    user?.isSchool
                        ? { myClassroomsOnly: true, includeClassrooms: true, take: 500 }
                        : { myProgrammesOnly: true, includeProgrammes: true, take: 500 },
                );
                setParticipants(response.items);
            } catch (error) {
                console.error("Failed to load participants", error);
            } finally {
                setLoading(false);
            }
        };

        if (user) {
            fetchParticipants();
        }
    }, [user]);

    const filteredParticipants = useMemo(() => {
        if (!searchQuery) return participants;
        const query = searchQuery.toLowerCase();
        return participants.filter(
            (p) =>
                p.name.toLowerCase().includes(query) || formatParticipantReference(p.id).toLowerCase().includes(query),
        );
    }, [participants, searchQuery]);

    const groupParticipants = useMemo(() => {
        if (user?.isSchool) {
            return [];
        }

        return (filteredParticipants as ParticipantWithProgrammes[]).filter((p) =>
            p.ParticipantToProgramme?.some((ptp) => ptp.Programme.isGroup),
        );
    }, [filteredParticipants, user?.isSchool]);

    const oneToOneParticipants = useMemo(() => {
        if (user?.isSchool) {
            return [];
        }

        return (filteredParticipants as ParticipantWithProgrammes[]).filter((p) =>
            p.ParticipantToProgramme?.some((ptp) => !ptp.Programme.isGroup),
        );
    }, [filteredParticipants, user?.isSchool]);

    const schoolParticipants = useMemo(() => {
        if (!user?.isSchool) {
            return [];
        }

        return filteredParticipants as ParticipantWithClassrooms[];
    }, [filteredParticipants, user?.isSchool]);

    const renderParticipantName = (participant: Participant) => (
        <>
            {user?.isSchool && participant.name !== formatParticipantReference(participant.id) && (
                <Typography variant="body2" color="textSecondary" display="block">
                    {participant.name}
                </Typography>
            )}
        </>
    );

    if (loading) {
        return <Spinner />;
    }

    return (
        <Box p={!isTablet && isMobile ? 2 : 4} width={1}>
            <Typography variant="h3" gutterBottom>
                My Participants
            </Typography>

            <Box mb={4}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search participants by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </Box>

            {user?.isSchool ? (
                <Box mb={4}>
                    <Typography
                        variant="h6"
                        color="textSecondary"
                        gutterBottom
                        sx={{ fontWeight: "bold", letterSpacing: 1 }}
                    >
                        CLASSROOM STUDENTS ({schoolParticipants.length})
                    </Typography>
                    <Paper
                        elevation={0}
                        sx={{ border: 1, borderColor: "borderShade.main", borderRadius: 2, overflow: "hidden" }}
                    >
                        <List disablePadding>
                            {schoolParticipants.length === 0 ? (
                                <Box p={3}>
                                    <Typography variant="body1" color="textSecondary">
                                        No classroom students found.
                                    </Typography>
                                </Box>
                            ) : (
                                schoolParticipants.map((p, index) => (
                                    <React.Fragment key={p.id}>
                                        <ListItem disablePadding>
                                            <ListItemButton
                                                onClick={() => push(`/participants/${p.id}`)}
                                                sx={{ py: 2, px: 3 }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="subtitle1" fontWeight="bold">
                                                            {formatParticipantReference(p.id)}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <>
                                                            {renderParticipantName(p)}
                                                            <Typography variant="body2" color="textSecondary">
                                                                {p.ParticipantToClassroom?.map(
                                                                    (ptc) => ptc.Classroom.name,
                                                                ).join(", ") || "No classroom assigned"}
                                                            </Typography>
                                                        </>
                                                    }
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                        {index < schoolParticipants.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))
                            )}
                        </List>
                    </Paper>
                </Box>
            ) : (
                <>
                    <Box mb={4}>
                        <Typography
                            variant="h6"
                            color="textSecondary"
                            gutterBottom
                            sx={{ fontWeight: "bold", letterSpacing: 1 }}
                        >
                            1-2-1 PARTICIPANTS ({oneToOneParticipants.length})
                        </Typography>
                        <Paper
                            elevation={0}
                            sx={{ border: 1, borderColor: "borderShade.main", borderRadius: 2, overflow: "hidden" }}
                        >
                            <List disablePadding>
                                {oneToOneParticipants.length === 0 ? (
                                    <Box p={3}>
                                        <Typography variant="body1" color="textSecondary">
                                            No 1-2-1 participants found.
                                        </Typography>
                                    </Box>
                                ) : (
                                    oneToOneParticipants.map((p, index) => (
                                        <React.Fragment key={p.id}>
                                            <ListItem disablePadding>
                                                <ListItemButton
                                                    onClick={() => push(`/participants/${p.id}`)}
                                                    sx={{ py: 2, px: 3 }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="subtitle1" fontWeight="bold">
                                                                {formatParticipantReference(p.id)}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography variant="body2" color="textSecondary">
                                                                {p.ParticipantToProgramme?.filter(
                                                                    (ptp) => !ptp.Programme.isGroup,
                                                                )
                                                                    .map((ptp) => ptp.Programme.name)
                                                                    .join(", ")}
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                            {index < oneToOneParticipants.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))
                                )}
                            </List>
                        </Paper>
                    </Box>

                    <Box mb={4}>
                        <Typography
                            variant="h6"
                            color="textSecondary"
                            gutterBottom
                            sx={{ fontWeight: "bold", letterSpacing: 1 }}
                        >
                            GROUP PARTICIPANTS ({groupParticipants.length})
                        </Typography>
                        <Paper
                            elevation={0}
                            sx={{ border: 1, borderColor: "borderShade.main", borderRadius: 2, overflow: "hidden" }}
                        >
                            <List disablePadding>
                                {groupParticipants.length === 0 ? (
                                    <Box p={3}>
                                        <Typography variant="body1" color="textSecondary">
                                            No group participants found.
                                        </Typography>
                                    </Box>
                                ) : (
                                    groupParticipants.map((p, index) => (
                                        <React.Fragment key={p.id}>
                                            <ListItem disablePadding>
                                                <ListItemButton
                                                    onClick={() => push(`/participants/${p.id}`)}
                                                    sx={{ py: 2, px: 3 }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="subtitle1" fontWeight="bold">
                                                                {formatParticipantReference(p.id)}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography variant="body2" color="textSecondary">
                                                                {p.ParticipantToProgramme?.filter(
                                                                    (ptp) => ptp.Programme.isGroup,
                                                                )
                                                                    .map((ptp) => ptp.Programme.name)
                                                                    .join(", ")}
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                            {index < groupParticipants.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))
                                )}
                            </List>
                        </Paper>
                    </Box>
                </>
            )}
        </Box>
    );
};
